import { t, Instance, flow, getSnapshot } from 'mobx-state-tree';
import { activityApi } from '@/api/activityWrapper';
import type {
  ActivityActor,
  ActivityCategory,
  ActivityEvent,
  ActivitySortOrder,
  FamilyActivityActorSearchParams,
  FamilyActivitySearchParams
} from '@/types/activity';

const ActivityEventModel = t.model('ActivityEvent', {
  id: t.identifier,
  familyId: t.maybe(t.string),
  action: t.string,
  targetType: t.string,
  targetId: t.maybe(t.string),
  visibility: t.enumeration(['family', 'actor_only']),
  actorUserId: t.maybe(t.string),
  actorDisplayName: t.maybe(t.string),
  targetLabel: t.maybe(t.string),
  summary: t.string,
  createdAt: t.string,
  navigationPath: t.maybe(t.string)
});

const ActivityActorModel = t.model('ActivityActor', {
  userId: t.identifier,
  displayName: t.string
});

const FamilyActivityFiltersModel = t.model('FamilyActivityFilters', {
  category: t.optional(t.enumeration(['all', 'people', 'media', 'invites', 'imports', 'tree', 'memories']), 'all'),
  userId: t.optional(t.string, ''),
  targetType: t.optional(t.string, ''),
  createdAfter: t.optional(t.string, ''),
  createdBefore: t.optional(t.string, ''),
  sortOrder: t.optional(t.enumeration(['asc', 'desc']), 'desc'),
  page: t.optional(t.number, 1),
  pageSize: t.optional(t.number, 20)
});

function toActivityEventModel(event: ActivityEvent): Instance<typeof ActivityEventModel> {
  return ActivityEventModel.create(event);
}

/** Drop duplicate audit ids (e.g. from a join multiplying rows) before MST attach. */
function dedupeActivityEvents(events: ActivityEvent[]): ActivityEvent[] {
  const seen = new Set<string>();
  return events.filter((event) => {
    if (!event.id || seen.has(event.id)) return false;
    seen.add(event.id);
    return true;
  });
}

function familySearchParamsFromFilters(
  filters: Instance<typeof FamilyActivityFiltersModel>,
  page: number
): FamilyActivitySearchParams {
  return {
    page,
    pageSize: filters.pageSize,
    sortOrder: filters.sortOrder as ActivitySortOrder,
    createdAfter: filters.createdAfter || undefined,
    createdBefore: filters.createdBefore || undefined,
    userId: filters.userId || undefined,
    targetType: filters.targetType || undefined,
    category: filters.category === 'all' ? undefined : (filters.category as Exclude<ActivityCategory, 'all'>)
  };
}

export const ActivityStore = t
  .model('ActivityStore', {
    familyItems: t.array(ActivityEventModel),
    familyTotal: t.optional(t.number, 0),
    familyActivityActors: t.array(ActivityActorModel),
    recentItems: t.array(ActivityEventModel),
    recentFamilyId: t.maybeNull(t.string),
    accountItems: t.array(ActivityEventModel),
    accountTotal: t.optional(t.number, 0),
    familyFilters: t.optional(FamilyActivityFiltersModel, {}),
    isLoadingFamily: t.optional(t.boolean, false),
    isLoadingMoreFamily: t.optional(t.boolean, false),
    isLoadingFamilyActors: t.optional(t.boolean, false),
    isLoadingRecent: t.optional(t.boolean, false),
    isLoadingAccount: t.optional(t.boolean, false),
    error: t.maybeNull(t.string)
  })
  .views((self) => ({
    get hasMoreFamily(): boolean {
      return self.familyItems.length < self.familyTotal;
    }
  }))
  .actions((self) => ({
    setFamilyFilter<K extends keyof Instance<typeof FamilyActivityFiltersModel>>(
      key: K,
      value: Instance<typeof FamilyActivityFiltersModel>[K]
    ) {
      self.familyFilters[key] = value;
      if (key === 'category') {
        self.familyFilters.targetType = '';
      }
      if (key !== 'page') {
        self.familyFilters.page = 1;
      }
    },

    resetFamilyFilters() {
      self.familyFilters.category = 'all';
      self.familyFilters.userId = '';
      self.familyFilters.targetType = '';
      self.familyFilters.createdAfter = '';
      self.familyFilters.createdBefore = '';
      self.familyFilters.sortOrder = 'desc';
      self.familyFilters.page = 1;
    },

    clearRecent() {
      self.recentItems.clear();
      self.recentFamilyId = null;
    },

    loadRecentFamily: flow(function* (familyId: string, options?: { pageSize?: number }) {
      if (!familyId) return;
      self.isLoadingRecent = true;
      self.error = null;
      try {
        const result = yield activityApi.listFamilyActivity(familyId, {
          page: 1,
          pageSize: options?.pageSize ?? 6,
          sortOrder: 'desc'
        });
        if (result.success) {
          self.recentFamilyId = familyId;
          self.recentItems.replace(dedupeActivityEvents(result.data.items).map(toActivityEventModel));
        } else {
          self.recentItems.clear();
          self.error = result.error.getUserMessage();
        }
      } catch (error) {
        self.recentItems.clear();
        self.error = error instanceof Error ? error.message : 'Failed to load recent activity';
      } finally {
        self.isLoadingRecent = false;
      }
    }),

    loadFamilyActivityActors: flow(function* (familyId: string, paramsOverride?: FamilyActivityActorSearchParams) {
      if (!familyId) return;
      self.isLoadingFamilyActors = true;

      const filters = getSnapshot(self.familyFilters);
      const params: FamilyActivityActorSearchParams =
        paramsOverride ??
        ({
          createdAfter: filters.createdAfter || undefined,
          createdBefore: filters.createdBefore || undefined,
          targetType: filters.targetType || undefined,
          category: filters.category === 'all' ? undefined : (filters.category as Exclude<ActivityCategory, 'all'>)
        } satisfies FamilyActivityActorSearchParams);

      try {
        const result = yield activityApi.listFamilyActivityActors(familyId, params);
        if (result.success) {
          self.familyActivityActors.replace(
            result.data.items.map((actor: ActivityActor) => ActivityActorModel.create(actor))
          );
          if (filters.userId && !result.data.items.some((actor: ActivityActor) => actor.userId === filters.userId)) {
            self.familyFilters.userId = '';
          }
        } else {
          self.familyActivityActors.clear();
        }
      } catch {
        self.familyActivityActors.clear();
      } finally {
        self.isLoadingFamilyActors = false;
      }
    }),

    loadFamilyActivity: flow(function* (familyId: string) {
      if (!familyId) return;
      self.isLoadingFamily = true;
      self.isLoadingMoreFamily = false;
      self.error = null;
      self.familyFilters.page = 1;

      const params = familySearchParamsFromFilters(self.familyFilters, 1);

      try {
        const result = yield activityApi.listFamilyActivity(familyId, params);
        if (result.success) {
          self.familyItems.replace(dedupeActivityEvents(result.data.items).map(toActivityEventModel));
          self.familyTotal = result.data.total;
          self.familyFilters.page = result.data.page;
        } else {
          self.familyItems.clear();
          self.familyTotal = 0;
          self.error = result.error.getUserMessage();
        }
      } catch (error) {
        self.familyItems.clear();
        self.familyTotal = 0;
        self.error = error instanceof Error ? error.message : 'Failed to load family activity';
      } finally {
        self.isLoadingFamily = false;
      }
    }),

    loadMoreFamilyActivity: flow(function* (familyId: string) {
      if (!familyId) return;
      if (self.isLoadingFamily || self.isLoadingMoreFamily || !self.hasMoreFamily) return;

      const nextPage = self.familyFilters.page + 1;
      self.isLoadingMoreFamily = true;
      self.error = null;

      const params = familySearchParamsFromFilters(self.familyFilters, nextPage);
      const existingIds = new Set(self.familyItems.map((item) => item.id));

      try {
        const result = yield activityApi.listFamilyActivity(familyId, params);
        if (result.success) {
          const fresh = dedupeActivityEvents(result.data.items).filter((event) => !existingIds.has(event.id));
          for (const event of fresh) {
            self.familyItems.push(toActivityEventModel(event));
          }
          self.familyTotal = result.data.total;
          self.familyFilters.page = result.data.page;
        } else {
          self.error = result.error.getUserMessage();
        }
      } catch (error) {
        self.error = error instanceof Error ? error.message : 'Failed to load more activity';
      } finally {
        self.isLoadingMoreFamily = false;
      }
    }),

    loadAccountActivity: flow(function* (options?: { page?: number; pageSize?: number }) {
      self.isLoadingAccount = true;
      self.error = null;
      try {
        const result = yield activityApi.listAccountActivity({
          page: options?.page ?? 1,
          pageSize: options?.pageSize ?? 10,
          sortOrder: 'desc'
        });
        if (result.success) {
          self.accountItems.replace(dedupeActivityEvents(result.data.items).map(toActivityEventModel));
          self.accountTotal = result.data.total;
        } else {
          self.accountItems.clear();
          self.accountTotal = 0;
          self.error = result.error.getUserMessage();
        }
      } catch (error) {
        self.accountItems.clear();
        self.accountTotal = 0;
        self.error = error instanceof Error ? error.message : 'Failed to load account activity';
      } finally {
        self.isLoadingAccount = false;
      }
    })
  }));

export type ActivityStoreType = Instance<typeof ActivityStore>;

export const activityStore = ActivityStore.create({});
